import React from 'react'
import { sdk } from '@/lib/sdk'
import type { ChannelUnread } from '@/types/channels'

/**
 * F-138 criterion 11: the unread state of every channel, for the menu (bold
 * name, mention count). One store per tab: the menu refreshes it, and the
 * open channel writes what `POST /read` answered, so the bold goes away at
 * once instead of on the next refresh.
 */
type Snapshot = Record<string, ChannelUnread>

let snapshot: Snapshot = {}
const listeners = new Set<() => void>()
let inFlight: Promise<void> | null = null
let again = false

const emit = () => listeners.forEach((listener) => listener())

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** Fetches `GET /channels/unread`; a call during a fetch runs once more after it. */
export const refreshChannelUnread = (): Promise<void> => {
  if (inFlight) { again = true; return inFlight }
  inFlight = sdk.channels.listUnread()
    .then((rows) => {
      const next: Snapshot = {}
      for (const row of rows) if (row?.spaceId) next[row.spaceId] = row
      snapshot = next
      emit()
    })
    .catch(() => undefined)
    .finally(() => {
      inFlight = null
      if (again) { again = false; void refreshChannelUnread() }
    })
  return inFlight
}

/** What the channel learned itself (`POST /read`, `GET /channels/{id}`). */
export const setChannelUnread = (row: ChannelUnread | null | undefined) => {
  if (!row?.spaceId) return
  snapshot = { ...snapshot, [row.spaceId]: row }
  emit()
}

export const clearChannelUnread = () => {
  snapshot = {}
  emit()
}

export const useChannelUnread = (spaceId: string | undefined): ChannelUnread | null => {
  const read = React.useCallback(() => (spaceId ? snapshot[spaceId] ?? null : null), [spaceId])
  return React.useSyncExternalStore(subscribe, read, read)
}

const readAll = () => snapshot

/** Every channel's unread state at once: the rail sums a section's channels on its icon (F-191 phase 2). */
export const useChannelUnreadAll = (): Readonly<Record<string, ChannelUnread>> =>
  React.useSyncExternalStore(subscribe, readAll, readAll)
